import { DestroyRef, effect, EffectRef, inject, Injectable, Injector, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import type { OfflineChannel } from '@microsoft/applicationinsights-offlinechannel-js';
import { APP_INSIGHTS_CONNECTION_STRING, CLIENT_TYPE_TOKEN, TELEMETRY_CONFIG, TelemetryConfig } from '@raptor-fe/core';
import { NetworkStatusService } from '../network-status-service/network-status.service';
import { createOfflineChannelConfig, OFFLINE_STATE, ONLINE_STATE, OnlineStateListener, purgeOfflineTelemetry } from './telemetry-offline';
import { scrubStringValuesIn, scrubUrlsIn } from './telemetry-privacy';

/** The version sent when the shell cannot supply one (local builds, the web preview of a native app). */
export const LOCAL_VERSION = 'local';

/** Errors raised before the SDK has loaded are kept, up to this many, and sent once it has. */
export const MAX_QUEUED_EXCEPTIONS = 20;

/**
 * Hosts that never receive trace headers: the Application Insights ingestion and live-metrics endpoints. The SDK
 * already skips its own endpoint; this keeps it so if the connection string points somewhere regional.
 */
export const CORRELATION_EXCLUDED_DOMAINS = ['*.applicationinsights.azure.com', '*.services.visualstudio.com'];

/** Options for {@link ApplicationInsightsService.start}. */
export interface TelemetryStartOptions {
    /**
     * The signed-in user's internal id (the token's user id), set as the authenticated user context. Never a username,
     * email address or display name.
     */
    authenticatedUserId?: string;
}

/**
 * The service's lifecycle. `idle` is the state before anything has decided whether telemetry is wanted: exceptions
 * are queued so errors during start-up are not lost. `stopped` is an explicit decision that it is not (signed out,
 * or the branch has it off): nothing is queued or sent.
 */
type TelemetryState = 'idle' | 'starting' | 'running' | 'stopped';

/**
 * The hosts of the given absolute URLs, for `correlationHeaderDomains`. Relative or malformed URLs are skipped, since
 * there is no host to correlate with.
 *
 * @param urls API base URLs from the environment.
 * @returns The distinct hosts (with port, when one is given).
 */
export function correlationDomains(urls: (string | undefined)[]): string[] {
    const hosts = new Set<string>();
    for (const url of urls) {
        if (!url) {
            continue;
        }
        try {
            hosts.add(new URL(url).host);
        } catch {
            continue;
        }
    }
    return [...hosts];
}

/**
 * The app version for telemetry from the shell's resolver. A failed or empty resolution falls back to 'local' rather
 * than blocking telemetry, since the version only labels items.
 *
 * @param config The shell's telemetry configuration, if any.
 * @returns The resolved version, or 'local'.
 */
export async function resolveTelemetryVersion(config: TelemetryConfig | null): Promise<string> {
    if (!config?.resolveVersion) {
        return LOCAL_VERSION;
    }
    try {
        return (await config.resolveVersion()) || LOCAL_VERSION;
    } catch {
        return LOCAL_VERSION;
    }
}

/**
 * Builds the telemetry initializer run on every item: the cloud role, the baseline attributes when the shell supplies
 * a {@link TelemetryConfig}, and URL scrubbing of every URL-like field (see telemetry-privacy.ts). Exception messages
 * and stacks are scrubbed too, because HTTP errors quote the request URL, query string included.
 *
 * @param roleName The `ai.cloud.role` tag; omitted when empty.
 * @param config The shell's telemetry identity and build values, if any.
 * @param version The app version from {@link resolveTelemetryVersion}.
 * @returns The initializer to register with the SDK.
 */
export function createTelemetryInitializer(
    roleName: string,
    config: TelemetryConfig | null,
    version: string,
): (item: ITelemetryItem) => boolean | void {
    return (item: ITelemetryItem) => {
        scrubStringValuesIn(item.data);
        item.tags = item.tags ?? {};
        if (roleName) {
            item.tags['ai.cloud.role'] = roleName;
        }
        if (config) {
            item.tags['ai.application.ver'] = version;
            item.data = {
                ...(item.data ?? {}),
                'service.namespace': config.serviceNamespace,
                'service.name': config.serviceName,
                'service.version': version,
                'lmg.business_unit': 'megabus',
                'deployment.environment.name': config.environment,
            };
        }

        if (typeof item.tags['ai.operation.name'] === 'string') {
            item.tags['ai.operation.name'] = scrubUrlsIn(item.tags['ai.operation.name']);
        }
        const trace = item.ext?.['trace'] as Record<string, unknown> | undefined;
        if (trace && typeof trace['name'] === 'string') {
            trace['name'] = scrubUrlsIn(trace['name']);
        }

        const baseData = item.baseData as Record<string, unknown> | undefined;
        if (!baseData) {
            return;
        }
        for (const field of ['uri', 'refUri', 'url', 'name', 'target', 'data', 'message']) {
            if (typeof baseData[field] === 'string') {
                baseData[field] = scrubUrlsIn(baseData[field] as string);
            }
        }
        scrubStringValuesIn(baseData['properties']);
        const exceptions = baseData['exceptions'];
        if (Array.isArray(exceptions)) {
            for (const exception of exceptions as Record<string, unknown>[]) {
                for (const field of ['message', 'stack']) {
                    if (typeof exception[field] === 'string') {
                        exception[field] = scrubUrlsIn(exception[field] as string);
                    }
                }
            }
        }
    };
}

/**
 * Client telemetry to Application Insights: page views on route changes, exceptions, and calls to our APIs with W3C
 * trace headers, so a tap in the app joins up with the API request it caused.
 *
 * {@link TelemetryPolicyService} decides when telemetry runs by calling {@link start} and {@link stop}: after a staff
 * sign-in while the branch has the shell's telemetry setting on, and, in a shell that opts in, for a customer session
 * once a code login succeeds. The SDK is imported dynamically, so it is not in the initial bundle and is never loaded
 * while telemetry is off. Without a connection string everything is a no-op (local and e2e builds, and tests).
 *
 * Telemetry produced while the device is offline is kept by the SDK's offline channel (IndexedDB, falling back to
 * localStorage; see telemetry-offline.ts) and sent once the network is back, including on the next start after the app
 * was closed while offline. The SDK's online state follows {@link NetworkStatusService}, which on native reads the
 * Capacitor Network plugin rather than the WebView's online events. Items are stored after the telemetry initializer
 * has run (the core runs initializers before any channel), so what is kept on the device is already scrubbed.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
    private readonly connectionString = inject(APP_INSIGHTS_CONNECTION_STRING, { optional: true }) ?? '';
    private readonly clientType = inject(CLIENT_TYPE_TOKEN, { optional: true }) ?? '';
    private readonly config = inject(TELEMETRY_CONFIG, { optional: true });
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);

    /** Emits on every {@link stop}, ending the route tracking of the run that stopped. */
    private readonly stopped = new Subject<void>();

    private state: TelemetryState = 'idle';
    private appInsights: ApplicationInsights | null = null;
    private offlineChannel: OfflineChannel | null = null;
    private networkStatusEffect: EffectRef | null = null;
    private starting: Promise<boolean> | null = null;
    private queuedExceptions: Error[] = [];

    /**
     * Incremented by every start and stop, so a start whose SDK import finishes after a stop (sign-out while the SDK
     * was still loading) knows it has been cancelled and loads nothing.
     */
    private generation = 0;

    /**
     * Loads the SDK and starts sending telemetry, then sends any exceptions queued before it loaded. Idempotent: while
     * a start is in progress the same promise is returned, and once running it resolves at once.
     *
     * @param options The authenticated user to attach to every item.
     * @returns Whether telemetry is running when the promise settles: false without a connection string, when the SDK
     *          failed to load, or when {@link stop} was called before it finished.
     */
    start(options: TelemetryStartOptions = {}): Promise<boolean> {
        if (!this.connectionString) {
            return Promise.resolve(false);
        }
        if (this.state === 'running') {
            return Promise.resolve(true);
        }
        if (this.state === 'starting' && this.starting) {
            return this.starting;
        }

        this.state = 'starting';
        const generation = ++this.generation;
        this.starting = this.load(generation, options).finally(() => {
            if (generation === this.generation) {
                this.starting = null;
            }
        });
        return this.starting;
    }

    /**
     * Stops telemetry: sends what has been collected if the network allows, unloads the SDK (removing its request and
     * error hooks), and clears the authenticated user. Queued exceptions are discarded and later calls send nothing
     * until {@link start} is called again. Cancels a start that is still loading.
     *
     * Every stop also deletes what the offline channel has stored, whether or not telemetry ran this session: a stop is
     * an explicit decision that telemetry is not wanted (sign-out, or a branch that has it off), so nothing kept on the
     * device may be sent later. That includes items left by an earlier run that was closed while offline: they are
     * replayed only when the next session starts telemetry, and dropped as soon as one stops it instead. The offline
     * channel is paused before the flush and unload, because both would otherwise write its in-memory items to storage.
     * Nothing is purged without a connection string, since nothing can have been stored.
     */
    stop(): void {
        this.generation++;
        this.state = 'stopped';
        this.starting = null;
        this.queuedExceptions = [];
        this.stopped.next();
        this.networkStatusEffect?.destroy();
        this.networkStatusEffect = null;

        const appInsights = this.appInsights;
        const offlineChannel = this.offlineChannel;
        this.appInsights = null;
        this.offlineChannel = null;
        if (appInsights) {
            try {
                appInsights.clearAuthenticatedUserContext();
                offlineChannel?.pause();
                appInsights.flush();
                appInsights.unload();
            } catch (error) {
                console.error('Telemetry failed to stop cleanly:', error);
            }
        }
        if (this.connectionString) {
            void purgeOfflineTelemetry(this.connectionString);
        }
    }

    /**
     * Logs an exception. Before telemetry has started the exception is queued, up to {@link MAX_QUEUED_EXCEPTIONS};
     * after a stop it is dropped.
     */
    trackException(error: unknown): void {
        if (!this.connectionString || this.state === 'stopped') {
            return;
        }
        const exception = error instanceof Error ? error : new Error(String(error));
        if (!this.appInsights) {
            if (this.queuedExceptions.length < MAX_QUEUED_EXCEPTIONS) {
                this.queuedExceptions.push(exception);
            }
            return;
        }
        this.appInsights.trackException({ exception });
    }

    /**
     * Logs a named event with optional property bag. No-op unless telemetry is running. Properties must never carry
     * free text, names, email addresses, barcode contents, photos or locations.
     */
    trackEvent(name: string, properties?: Record<string, string>): void {
        this.appInsights?.trackEvent({ name }, properties);
    }

    /**
     * Logs a page view. No-op unless telemetry is running.
     */
    trackPageView(name?: string, uri?: string): void {
        this.appInsights?.trackPageView({ name, uri });
    }

    /**
     * Logs a trace message at the specified severity level. No-op unless telemetry is running.
     */
    trackTrace(message: string, severityLevel?: number): void {
        this.appInsights?.trackTrace({ message, severityLevel });
    }

    /**
     * Cookies are off: the app sets the authenticated user itself, and an anonymous id persisted in the WebView adds a
     * device identifier the work item does not need. Headers are never captured because requests carry bearer tokens.
     * The Sender's session-storage buffer is off: the offline channel is what keeps items across restarts, and a
     * session-storage copy would outlive a {@link stop} and be sent by the next start in the same WebView.
     *
     * The offline channel finishes configuring itself on the event-loop turn after the SDK loads (its documented
     * behaviour), so the first page view and the queued exceptions wait for that turn; otherwise, when offline, they
     * would bypass it and be lost if the app were closed before the network returned.
     */
    private async load(generation: number, options: TelemetryStartOptions): Promise<boolean> {
        try {
            const [{ ApplicationInsights, DistributedTracingModes }, { OfflineChannel, StorageProviders }, version] = await Promise.all([
                import('@microsoft/applicationinsights-web'),
                import('@microsoft/applicationinsights-offlinechannel-js'),
                resolveTelemetryVersion(this.config),
            ]);
            if (generation !== this.generation) {
                return false;
            }

            const correlationHosts = correlationDomains(this.config?.correlationUrls ?? []);
            const offlineChannel = new OfflineChannel();
            const appInsights = new ApplicationInsights({
                config: {
                    connectionString: this.connectionString,
                    enableAutoRouteTracking: false,
                    distributedTracingMode: DistributedTracingModes.W3C,
                    enableCorsCorrelation: correlationHosts.length > 0,
                    correlationHeaderDomains: correlationHosts,
                    correlationHeaderExcludedDomains: CORRELATION_EXCLUDED_DOMAINS,
                    enableRequestHeaderTracking: false,
                    enableResponseHeaderTracking: false,
                    enableAjaxErrorStatusText: false,
                    disableCookiesUsage: true,
                    enableSessionStorageBuffer: false,
                    extensions: [offlineChannel],
                    extensionConfig: {
                        [offlineChannel.identifier]: createOfflineChannelConfig(StorageProviders),
                    },
                },
            });
            appInsights.addTelemetryInitializer(createTelemetryInitializer(this.config?.roleName ?? this.clientType, this.config, version));
            appInsights.loadAppInsights();
            if (options.authenticatedUserId) {
                appInsights.setAuthenticatedUserContext(options.authenticatedUserId, undefined, false);
            }
            this.appInsights = appInsights;
            this.offlineChannel = offlineChannel;
            this.followNetworkStatus([appInsights.getSender()?.getOfflineListener(), offlineChannel.getOfflineListener()]);

            await new Promise((resolve) => setTimeout(resolve, 0));
            if (generation !== this.generation) {
                return false;
            }
            this.state = 'running';

            this.trackRoutes(appInsights);
            const queued = this.queuedExceptions;
            this.queuedExceptions = [];
            for (const exception of queued) {
                appInsights.trackException({ exception });
            }
            return true;
        } catch (error) {
            console.error('Telemetry failed to start:', error);
            if (generation === this.generation) {
                this.state = 'stopped';
                this.queuedExceptions = [];
            }
            return false;
        }
    }

    /**
     * Keeps the SDK's online state on what {@link NetworkStatusService} reports, for as long as this run lasts: the
     * Sender's listener, so it holds its batch rather than retrying into a dead network, and the offline channel's, which
     * decides whether an item is stored or handed to the Sender and when stored items are sent. The SDK's own listeners
     * hear only the WebView's online and offline events, which a native app does not reliably get; it counts the device
     * as offline if either source says so. The current state is applied at once, before anything is tracked, and the
     * effect is destroyed by {@link stop}. The network service is resolved here rather than injected, so it is only
     * created once telemetry starts.
     */
    private followNetworkStatus(listeners: (OnlineStateListener | null | undefined)[]): void {
        const networkStatus = this.injector.get(NetworkStatusService);
        const active = listeners.filter((listener): listener is OnlineStateListener => !!listener);
        const apply = (connected: boolean) => {
            for (const listener of active) {
                listener.setOnlineState(connected ? ONLINE_STATE : OFFLINE_STATE);
            }
        };
        apply(untracked(networkStatus.connected));
        this.networkStatusEffect = effect(() => apply(networkStatus.connected()), { injector: this.injector });
    }

    /**
     * A page view per completed navigation, from the Angular router rather than the History API, so the view is the
     * route Angular settled on. The SDK loads after sign-in, long after the first navigation, so the current route is
     * recorded at once; the Angular plugin assumes it starts before the first navigation and would miss it. The
     * operation name starts as the page URL, so it is reset to the route (and scrubbed again by the initializer).
     * Tracking ends by itself on {@link stop} or when the injector is destroyed, so no subscription is held. It cannot
     * end after one page view (take(1)): it records every navigation for as long as telemetry runs.
     */
    private trackRoutes(appInsights: ApplicationInsights): void {
        const trackRoute = () => {
            const trace = appInsights.context?.telemetryTrace;
            if (trace) {
                trace.name = scrubUrlsIn(this.router.url);
            }
            appInsights.trackPageView({ uri: this.router.url });
        };
        if (this.router.navigated) {
            trackRoute();
        }
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this.stopped),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(trackRoute);
    }
}
